import type { TicketCounts } from '@/api/ticketsApi'

/** The counts that are a single number (not the per-product map). */
type CountKey = Exclude<keyof TicketCounts, 'byProduct'>

/** A queue in the sidebar; `to` carries the queue as a query param on /queue. */
export interface QueueNav {
  key: string
  labelKey: string
  to: string
  count: CountKey
  /** The count is a warning (shown in amber). */
  warn?: boolean
}

export const QUEUES: QueueNav[] = [
  { key: 'all', labelKey: 'nav.allOpen', to: '/queue', count: 'allOpen' },
  { key: 'mine', labelKey: 'nav.assignedToMe', to: '/queue?queue=mine', count: 'assignedToMe' },
  { key: 'sla_risk', labelKey: 'nav.slaAtRisk', to: '/queue?queue=sla_risk', count: 'slaAtRisk', warn: true },
  { key: 'gxp', labelKey: 'nav.gxpImpacting', to: '/queue?queue=gxp', count: 'gxpImpacting' },
  { key: 'escalated', labelKey: 'nav.escalated', to: '/queue?queue=escalated', count: 'escalated' },
]

/** A product as the sidebar and top bar need it: `key` is the `?product=` value, from `GET /products`. */
export interface NavProduct {
  key: string
  name: string
}

/** One step of the top bar's "where you are": an i18n key, or text as it is (a ticket key). */
export interface Crumb {
  labelKey?: string
  text?: string
  to?: string
}

const ADMIN: Record<string, string> = {
  '/analytics': 'nav.analytics',
  '/admin/sla': 'nav.slaPolicies',
  '/admin/products': 'nav.catalogProducts',
  '/admin/clients': 'nav.catalogClients',
  '/admin/access': 'nav.access',
}

/**
 * Where the user is, for the top bar (z8nrz7fv5m): the section, then the page.
 * A customer's only queue is their organisation's open tickets. `products` is
 * the catalog (`GET /products`); until it loads, a product queue shows its key.
 */
export function topBarTrail(pathname: string, search: string, isCustomer = false, products: NavProduct[] = []): Crumb[] {
  const tickets: Crumb = { labelKey: 'nav.tickets', to: '/queue' }

  if (pathname === '/queue') {
    const params = new URLSearchParams(search)
    const productKey = params.get('product')
    if (productKey) return [tickets, { text: products.find((p) => p.key === productKey)?.name ?? productKey }]
    if (isCustomer) return [tickets, { labelKey: 'nav.myOrgTickets' }]
    const queue = QUEUES.find((q) => q.key === (params.get('queue') ?? 'all')) ?? QUEUES[0]
    return [tickets, { labelKey: queue.labelKey }]
  }
  if (pathname === '/boards') return [tickets, { labelKey: 'nav.boards' }]
  // A queue's cards (its built-in board) sit where the queue does.
  const queueBoard = QUEUES.find((q) => pathname === `/boards/queue-${q.key}`)
  if (queueBoard) return [tickets, { labelKey: queueBoard.labelKey }]
  if (/^\/boards\/[^/]+$/.test(pathname)) return [tickets, { labelKey: 'nav.boards', to: '/boards' }, { labelKey: 'nav.board' }]
  if (pathname === '/tickets/new') return [tickets, { labelKey: isCustomer ? 'nav.newTicket' : 'nav.logTicket' }]
  const ticket = /^\/tickets\/([^/]+)$/.exec(pathname)
  if (ticket) return [tickets, { text: decodeURIComponent(ticket[1]) }]

  const section = Object.keys(ADMIN).find((p) => pathname === p || pathname.startsWith(`${p}/`))
  if (section) {
    const page: Crumb = { labelKey: ADMIN[section], to: section }
    // A product or client opened from its list: the list is the last step, as a link back.
    return [{ labelKey: 'nav.admin' }, pathname === section ? { labelKey: ADMIN[section] } : page]
  }
  return []
}
